import type {
  DashboardInput,
  DashboardPeriod,
  DashboardSeries,
} from '@sirius/contracts/admin'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import type { AdminClient } from '~/api/adminClient'
import { useAdminClient } from '~/api/adminClientContext'
import { QueryErrorBoundary } from '~/components/queryErrorBoundary'
import { Button } from '~/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/card'
import type { ChartConfig } from '~/components/ui/chart'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~/components/ui/chart'
import { Skeleton } from '~/components/ui/skeleton'
import { formatCredits, formatCreditsCompact } from '~/i18n/format'
import { errorMessage } from '~/providers/errors'
import {
  formatBucketLabel,
  formatBucketTitle,
  formatCount,
} from './dashboardFormat'

export type DashboardCardKind = 'profit' | 'credits' | 'activeCabinets'

type CardDefinition = {
  /** what the card counts, under its title */
  description: (t: TFunction, period: DashboardPeriod) => string
  /** what the total is, next to it */
  totalLabel: (t: TFunction, until: Date | undefined) => string
  chart: 'bar' | 'area'
  /** an amount of credits (two decimals, stored units) or a plain count */
  unit: 'credits' | 'count'
  /** a theme colour (`index.css`), so the chart follows light and dark */
  color: string
  /** the procedure, picked explicitly (oRPC clients are proxies) */
  load: (client: AdminClient, input: DashboardInput) => Promise<DashboardSeries>
}

const hourly = (period: DashboardPeriod) => period === '24h'

export const DASHBOARD_CARDS: Record<DashboardCardKind, CardDefinition> = {
  profit: {
    description: (t, period) =>
      hourly(period)
        ? t('dashboardPage.cards.profit.descriptionHour')
        : t('dashboardPage.cards.profit.descriptionDay'),
    totalLabel: (t) => t('dashboardPage.cards.totalInPeriod'),
    chart: 'bar',
    unit: 'credits',
    color: 'var(--chart-1)',
    load: (client, input) => client.dashboard.profit(input),
  },
  credits: {
    description: (t, period) =>
      hourly(period)
        ? t('dashboardPage.cards.credits.descriptionHour')
        : t('dashboardPage.cards.credits.descriptionDay'),
    totalLabel: (t, until) =>
      until === undefined
        ? t('dashboardPage.cards.totalNow')
        : t('dashboardPage.cards.totalAtEnd'),
    chart: 'area',
    unit: 'credits',
    color: 'var(--chart-2)',
    load: (client, input) => client.dashboard.credits(input),
  },
  activeCabinets: {
    description: (t, period) =>
      hourly(period)
        ? t('dashboardPage.cards.activeCabinets.descriptionHour')
        : t('dashboardPage.cards.activeCabinets.descriptionDay'),
    totalLabel: (t) => t('dashboardPage.cards.totalInPeriod'),
    chart: 'bar',
    unit: 'count',
    color: 'var(--chart-3)',
    load: (client, input) => client.dashboard.activeCabinets(input),
  },
}

const cardTitle = (t: TFunction, kind: DashboardCardKind) =>
  t(`dashboardPage.cards.${kind}.title`)

const CHART_CLASS = 'aspect-auto h-48 w-full'

/** A card's total and chart, loaded with suspense. */
function CardBody({
  kind,
  input,
}: {
  kind: DashboardCardKind
  input: DashboardInput
}) {
  const client = useAdminClient()
  const { t } = useTranslation()
  const card = DASHBOARD_CARDS[kind]
  const title = cardTitle(t, kind)
  const { data } = useSuspenseQuery({
    queryKey: ['dashboard', kind, input],
    queryFn: () => card.load(client, input),
  })
  const credits = card.unit === 'credits'
  const formatValue = (value: number) =>
    credits ? formatCredits(value) : formatCount(value)
  const points = data.series.map((point) => ({
    label: formatBucketLabel(point.bucketStart, input.period),
    title: formatBucketTitle(point.bucketStart, input.period),
    value: point.value,
  }))
  const config: ChartConfig = {
    value: { label: title, color: card.color },
  }
  const axes = (
    <>
      <CartesianGrid vertical={false} />
      <XAxis
        dataKey="label"
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        minTickGap={16}
      />
      <YAxis
        tickLine={false}
        axisLine={false}
        width="auto"
        allowDecimals={credits}
        tickFormatter={(value: number) =>
          credits ? formatCreditsCompact(value) : formatCount(value)
        }
      />
      <ChartTooltip
        cursor={card.chart === 'area'}
        content={
          <ChartTooltipContent
            labelFormatter={(_label, payload) => {
              const title: unknown = payload[0]?.payload?.title
              return typeof title === 'string' ? title : null
            }}
            formatter={(value) => (
              <span className="flex w-full items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-[2px]"
                  style={{ background: card.color }}
                />
                <span className="text-muted-foreground flex-1">{title}</span>
                <span className="font-mono font-medium tabular-nums">
                  {typeof value === 'number' ? formatValue(value) : null}
                </span>
              </span>
            )}
          />
        }
      />
    </>
  )
  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-baseline gap-2">
        <span
          data-testid="dashboard-total"
          className="text-3xl font-semibold tabular-nums"
        >
          {formatValue(data.total)}
        </span>
        <span className="text-muted-foreground text-sm">
          {card.totalLabel(t, input.until)}
        </span>
      </p>
      <ChartContainer
        config={config}
        className={CHART_CLASS}
      >
        {card.chart === 'bar' ? (
          <BarChart
            data={points}
            accessibilityLayer
          >
            {axes}
            <ReferenceLine
              y={0}
              stroke="var(--border)"
            />
            <Bar
              dataKey="value"
              fill="var(--color-value)"
              radius={4}
              isAnimationActive={false}
            />
          </BarChart>
        ) : (
          <AreaChart
            data={points}
            accessibilityLayer
          >
            {axes}
            <Area
              dataKey="value"
              type="linear"
              stroke="var(--color-value)"
              strokeWidth={2}
              fill="var(--color-value)"
              fillOpacity={0.15}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
      </ChartContainer>
    </div>
  )
}

function CardSkeleton() {
  return (
    <div
      className="flex flex-col gap-4"
      aria-busy="true"
    >
      <Skeleton className="h-9 w-32" />
      <Skeleton className={CHART_CLASS} />
    </div>
  )
}

/**
 * One dashboard card (a region named by its title): the total and a chart
 * per bucket. It loads on its own, so a slow or failed card leaves the others.
 */
export function DashboardCard({
  kind,
  input,
}: {
  kind: DashboardCardKind
  input: DashboardInput
}) {
  const { t } = useTranslation()
  const card = DASHBOARD_CARDS[kind]
  const title = cardTitle(t, kind)
  return (
    <Card
      role="region"
      aria-label={title}
      className="min-w-0"
    >
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{card.description(t, input.period)}</CardDescription>
      </CardHeader>
      <CardContent>
        <QueryErrorBoundary
          resetKey={JSON.stringify(input)}
          fallback={({ error, retry }) => (
            <div className="flex flex-col items-start gap-3">
              <p
                role="alert"
                className="text-destructive text-sm"
              >
                {t(`dashboardPage.cards.${kind}.loadError`, {
                  reason:
                    errorMessage(error) ??
                    t('dashboardPage.cards.unknownError'),
                })}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={retry}
              >
                {t('dashboardPage.cards.retry')}
              </Button>
            </div>
          )}
        >
          <Suspense fallback={<CardSkeleton />}>
            <CardBody
              kind={kind}
              input={input}
            />
          </Suspense>
        </QueryErrorBoundary>
      </CardContent>
    </Card>
  )
}
